import React, { Component } from 'react'
import {
    Card, Table, Button, Drawer, Form, Input, Select, Space, message,
    Popconfirm, Typography, Row, Col, Tag, Spin
} from 'antd'
import {
    PlusOutlined, EditOutlined, DeleteOutlined, RocketOutlined, ReloadOutlined, ThunderboltOutlined, CheckCircleOutlined, StopOutlined, CodeOutlined
} from '@ant-design/icons'
import {
    getMcpToolList, saveMcpTool, updateMcpTool,
    saveMcpToolVersion, publishMcpToolVersion, getMcpToolVersionList,
    saveApiRegistry, updateApiRegistry, getApiRegistryList, createToolCombo
} from '../../request'

const { Text } = Typography
const { TextArea } = Input

// 极大简化的极客一站式工作台
export default class McpTool extends Component {
    formRef = React.createRef()

    state = {
        dataSource: [],
        apiDataSource: [],
        loading: false,
        saving: false,
        drawerVisible: false,
        editRecord: null,
        parsedParams: [],
        parsedOutputs: []
    }

    componentDidMount() {
        this.loadList()
        this.loadApiList()
    }

    getServiceId = () => {
        const params = new URLSearchParams(window.location.search)
        return params.get('serviceId') ? Number(params.get('serviceId')) : undefined
    }

    loadList = () => {
        this.setState({ loading: true })
        const serviceId = this.getServiceId()
        getMcpToolList(serviceId ? { serviceId } : {})
            .then(res => this.setState({ dataSource: Array.isArray(res) ? res : (res?.list || []) }))
            .catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    loadApiList = () => {
        getApiRegistryList({})
            .then(res => this.setState({ apiDataSource: Array.isArray(res) ? res : (res?.list || []) }))
            .catch(() => {})
    }

    openCreate = () => {
        this.setState({ drawerVisible: true, editRecord: null, parsedParams: [], parsedOutputs: [] })
        setTimeout(() => {
            if (this.formRef.current) {
                this.formRef.current.resetFields()
                this.formRef.current.setFieldsValue({
                    method: 'POST', category: '其他'
                })
            }
        }, 0)
    }

    openEdit = async (record) => {
        this.setState({ drawerVisible: true, editRecord: record, parsedParams: [], parsedOutputs: [] })
        
        // 我们尝试拉取最新的 Version，然后还原表单
        try {
            const versions = await getMcpToolVersionList({ toolId: record.id })
            const activeVersion = versions && versions.length > 0 ? versions[0] : null
            
            // 匹配 API 信息
            const matchedApi = this.state.apiDataSource.find(api => api.apiCode === record.apiCode)

            // 从版本spec中获取description（tool列表可能不返回description）
            const specDescription = activeVersion?.spec?.description || ''

            setTimeout(() => {
                if (this.formRef.current) {
                    this.formRef.current.setFieldsValue({
                        name: record.name || record.toolName || (activeVersion?.spec?.name) || '',
                        toolCode: record.toolCode,
                        description: record.description || specDescription || '',
                        category: record.category || '其他',
                        apiCode: record.apiCode || undefined,
                        method: matchedApi?.method || record.method || 'POST',
                        url: matchedApi ? ((matchedApi.baseUrl || '') + (matchedApi.path || '')) : (record.url || ''),
                    })
                }
            }, 0)

            if (activeVersion && activeVersion.spec && activeVersion.spec.inputSchema) {
                const props = activeVersion.spec.inputSchema.properties || {}
                const params = Object.keys(props).map(k => ({
                    paramName: k,
                    paramType: props[k]?.type || 'string',
                    description: props[k]?.description || ''
                }))
                this.setState({ parsedParams: params })
            }
            if (activeVersion && activeVersion.spec && activeVersion.spec.outputSchema) {
                const props = activeVersion.spec.outputSchema.properties || {}
                const outputs = Object.keys(props).map(k => ({
                    paramName: k,
                    paramType: props[k]?.type || 'string',
                    description: props[k]?.description || ''
                }))
                this.setState({ parsedOutputs: outputs })
            }
        } catch (error) {
            console.error(error)
        }
    }

    handleApiSelect = (apiCode) => {
        if (!apiCode) return
        const { apiDataSource } = this.state
        const api = apiDataSource.find(a => a.apiCode === apiCode)
        if (!api) return

        let newParams = []
        let jsonExampleObj = {}
        if (api.requestSchema) {
            try {
                const schema = typeof api.requestSchema === 'string' ? JSON.parse(api.requestSchema) : api.requestSchema
                if (schema) {
                    // 支持标准的 JSON Schema，也宽容用户直接贴一段 JSON 示例对象
                    let props = schema.properties;
                    if (!props && schema.type !== 'object' && Object.keys(schema).length > 0) {
                        props = {};
                        for (let k in schema) {
                            props[k] = { type: typeof schema[k] };
                        }
                    }
                    
                    if (props) {
                        newParams = Object.keys(props).map(k => {
                            const type = props[k]?.type || 'string'
                            
                            // 尽力提供一个用于占位的简单示例值
                            if (props === schema.properties) {
                                if (type === 'string') jsonExampleObj[k] = ""
                                else if (type === 'number' || type === 'integer') jsonExampleObj[k] = 0
                                else if (type === 'boolean') jsonExampleObj[k] = false
                                else if (type === 'array') jsonExampleObj[k] = []
                                else jsonExampleObj[k] = {}
                            } else {
                                // 宽容模式下，用户直接提供的值就作为示例
                                jsonExampleObj[k] = schema[k]
                            }
                            
                            return {
                                paramName: k,
                                paramType: type === 'object' && Array.isArray(schema[k]) ? 'array' : type,
                                description: props[k]?.description || 'API带入参数'
                            }
                        })
                    }
                }
            } catch(e) {
                console.error("Failed to parse requestSchema", e)
            }
        }

        const currentDesc = this.formRef.current?.getFieldValue('description') || ''
        const newDesc = api.description ? (currentDesc ? `${currentDesc}\n${api.description}` : api.description) : currentDesc

        let updates = {
            method: api.method || 'POST',
            url: (api.baseUrl || '') + (api.path || ''),
            description: newDesc
        }

        const currentName = this.formRef.current?.getFieldValue('name')
        const currentToolCode = this.formRef.current?.getFieldValue('toolCode')
        if (!currentName) updates.name = api.apiName || ''
        if (!currentToolCode) updates.toolCode = api.apiCode || ''

        if (Object.keys(jsonExampleObj).length > 0) {
            updates.jsonExample = JSON.stringify(jsonExampleObj, null, 2)
        }

        if (this.formRef.current) {
            this.formRef.current.setFieldsValue(updates)
        }

        if (newParams.length > 0) {
            this.setState({ parsedParams: newParams })
            message.success('已自动带入接口配置及参数模板')
        } else {
            message.success('已自动带入接口配置')
        }
    }

    autoParseJson = () => {
        const jsonStr = this.formRef.current?.getFieldValue('jsonExample')
        if (!jsonStr) {
            message.warning('请先贴入 JSON 请求示例')
            return
        }
        try {
            const obj = JSON.parse(jsonStr)
            const mapType = (val) => {
                if (Array.isArray(val)) return 'array'
                if (typeof val === 'number') return Number.isInteger(val) ? 'integer' : 'number'
                return typeof val
            }
            const params = Object.keys(obj).map(k => ({
                paramName: k,
                paramType: mapType(obj[k]),
                description: '自动解析参数'
            }))
            this.setState({ parsedParams: params })
            message.success('已自动解析提取参数')
            // 清空文本框保持整洁
            if (this.formRef.current) {
                this.formRef.current.setFieldsValue({ jsonExample: '' })
            }
        } catch(e) {
            message.error('JSON格式不合法')
        }
    }

    handleParamChange = (index, field, value) => {
        const { parsedParams } = this.state
        const newParams = [...parsedParams]
        newParams[index][field] = value
        this.setState({ parsedParams: newParams })
    }

    removeParam = (index) => {
        const { parsedParams } = this.state
        const newParams = [...parsedParams]
        newParams.splice(index, 1)
        this.setState({ parsedParams: newParams })
    }

    addEmptyParam = () => {
        const { parsedParams } = this.state
        this.setState({ parsedParams: [...parsedParams, { paramName: '', paramType: 'string', description: '' }] })
    }

    handleOutputChange = (index, field, value) => {
        const { parsedOutputs } = this.state
        const newOutputs = [...parsedOutputs]
        newOutputs[index][field] = value
        this.setState({ parsedOutputs: newOutputs })
    }

    removeOutput = (index) => {
        const { parsedOutputs } = this.state
        const newOutputs = [...parsedOutputs]
        newOutputs.splice(index, 1)
        this.setState({ parsedOutputs: newOutputs })
    }

    addEmptyOutput = () => {
        const { parsedOutputs } = this.state
        this.setState({ parsedOutputs: [...parsedOutputs, { paramName: '', paramType: 'string', description: '' }] })
    }

    handleSaveAndLive = async (values) => {
        this.setState({ saving: true })
        try {
            const { parsedParams } = this.state
            
            // 1. 解析URL (例如 https://gateway.com/v1/user)
            let baseUrl = values.url
            let path = '/'
            try {
                const urlObj = new URL(values.url)
                baseUrl = urlObj.origin
                path = urlObj.pathname + urlObj.search
            } catch(e) {
                // Ignore, fallback
            }

            // 2. 构造聚合请求体发送给后端的 Combo 接口
            const { parsedOutputs } = this.state
            const payload = {
                serviceId: this.getServiceId(),
                toolCode: values.toolCode,
                name: values.name,
                description: values.description,
                category: values.category || '其他',
                method: values.method.toUpperCase(),
                baseUrl,
                path,
                params: parsedParams.map(p => ({
                    paramName: p.paramName,
                    paramType: p.paramType || 'string',
                    description: p.description
                })),
                outputs: parsedOutputs.map(p => ({
                    paramName: p.paramName,
                    paramType: p.paramType || 'string',
                    description: p.description
                }))
            }

            // 3. 一键调用超级接口，后端完成所有事务
            await createToolCombo(payload)

            message.success('Tool 已自动保存并在远端一键发布！')
            this.setState({ drawerVisible: false })
            this.loadList()

        } catch (error) {
            console.error(error)
            message.error('自动编排保存失败，请检查网络或联系管理员')
        } finally {
            this.setState({ saving: false })
        }
    }

    render() {
        const { dataSource, loading, saving, drawerVisible, parsedParams, apiDataSource } = this.state
        
        const apiGroups = {}
        apiDataSource.forEach(api => {
            const sys = api.sourceSystem || '未分类 (Default)'
            if (!apiGroups[sys]) apiGroups[sys] = []
            apiGroups[sys].push(api)
        })

        const columns = [
            {
                title: '工具资产',
                key: 'tool',
                render: (_, r) => (
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 16 }}>{r.name || r.toolName}</div>
                        <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>{r.description || '暂无描述'}</div>
                        <Space style={{ marginTop: 8 }}>
                            <Tag color="blue">{r.category || '其他'}</Tag>
                            <Tag color="cyan">{r.toolCode}</Tag>
                            {r.status === 'published' ? <Tag color="green"><CheckCircleOutlined/> 一键在线</Tag> : <Tag color="default"><StopOutlined/> 停用</Tag>}
                        </Space>
                    </div>
                )
            },
            {
                title: '维护操作',
                key: 'action',
                width: 180,
                render: (_, r) => (
                    <Space size={12}>
                        <Button type="primary" ghost icon={<EditOutlined />} onClick={() => this.openEdit(r)}>设置</Button>
                    </Space>
                )
            }
        ]

        return (
            <div style={{ padding: 16 }}>
                <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                    <Col>
                        <div style={{ fontSize: 24, fontWeight: 700 }}>AI 能力插件构建台</div>
                        <div style={{ color: '#666', marginTop: 4 }}>一站式“即插即用”工作流，抛弃繁重的版控流转，所见即所得。</div>
                    </Col>
                    <Col>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={this.loadList}>刷新</Button>
                            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={this.openCreate}>快速创建 Tool</Button>
                        </Space>
                    </Col>
                </Row>

                <Card bordered={false} style={{ borderRadius: 16 }}>
                    <Table 
                        dataSource={dataSource} 
                        columns={columns} 
                        rowKey="id" 
                        loading={loading}
                        pagination={{ pageSize: 20 }}
                    />
                </Card>

                <Drawer
                    title={this.state.editRecord ? <span><span role="img" aria-label="edit">⚙️</span> 调整工具</span> : <span><span role="img" aria-label="new">✨</span> 新建魔法工具</span>}
                    width={680}
                    visible={drawerVisible}
                    onClose={() => this.setState({ drawerVisible: false })}
                    footer={null}
                    destroyOnClose
                >
                    <Form ref={this.formRef} layout="vertical" onFinish={this.handleSaveAndLive} onFinishFailed={() => message.warning('保存失败：请往上滑动，检查上面的必填项（红星指示）是否已填写完整！')}>
                        {/* 从已有接口快速导入 */}
                        <div style={{ background: '#eef6ff', padding: 16, borderRadius: 12, marginBottom: 24, border: '1px solid #dbe7ff' }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#175ec9' }}>
                                <span role="img" aria-label="magic">✨</span> 快速从接口库构建
                            </div>
                            <Form.Item name="apiCode" label="选择一个已注册API，自动带入 URL、方法、参数和说明" style={{ marginBottom: 0 }}>
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="按所属系统分类，支持搜索..."
                                    onChange={this.handleApiSelect}
                                    optionFilterProp="label"
                                    size="large"
                                >
                                    {Object.keys(apiGroups).map(sys => (
                                        <Select.OptGroup label={sys} key={sys}>
                                            {apiGroups[sys].map(api => (
                                                <Select.Option key={api.apiCode} value={api.apiCode} label={`${api.apiName} ${api.apiCode}`}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <span>{api.apiName}</span>
                                                        <span style={{ color: '#999', fontSize: 12 }}>{api.apiCode}</span>
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select.OptGroup>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>

                        {/* 基础配置 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>1. 基础认知 (AI如何找到它)</div>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="toolCode" label="唯一标识代码 (英文字母+下划线)" rules={[{ required: true }]}>
                                        <Input placeholder="eg: weather_query" disabled={!!this.state.editRecord} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="name" label="工具展示名称" rules={[{ required: true }]}>
                                        <Input placeholder="eg: 全球天气查询仪" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name="description" label="详细Prompt描述 (极为重要，决定大模型是否正确调用)" rules={[{ required: true }]}>
                                <TextArea rows={3} placeholder="告诉模型这个工具是做什么的，在何种上下文中调用它。例如：当用户询问任何关于天气的状况时，调用此工具。" />
                            </Form.Item>
                        </div>

                        {/* 直连配置 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>2. 接口直达 (从哪里获取果实)</div>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item name="method" label="HTTP方式" rules={[{ required: true }]}>
                                        <Select options={['GET','POST','PUT','DELETE'].map(x => ({label:x, value:x}))} />
                                    </Form.Item>
                                </Col>
                                <Col span={18}>
                                    <Form.Item name="url" label="完整 API URL" rules={[{ required: true, type: 'url' }]}>
                                        <Input placeholder="https://api.example.com/v1/weather" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>

                        {/* 参数极简生成器 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
                                3. 模型输入参数 (无需繁琐填写，一键提取)
                            </div>
                            
                            <Form.Item name="jsonExample" label="贴入一段完整请求JSON模板 (可选)">
                                <TextArea rows={3} placeholder='{"city": "Beijing", "days": 3}' />
                            </Form.Item>
                            <Button size="small" type="primary" ghost icon={<CodeOutlined />} onClick={this.autoParseJson} style={{ marginBottom: 16 }}>
                                万物识别：自动生成下方参数表
                            </Button>

                            <Table 
                                size="small" 
                                pagination={false}
                                dataSource={parsedParams}
                                rowKey={(r, idx) => idx}
                                columns={[
                                    { title: '参数名称', dataIndex: 'paramName', width: 140, render: (v, r, idx) => <Input size="small" value={v} onChange={e => this.handleParamChange(idx, 'paramName', e.target.value)} /> },
                                    { title: '类型', dataIndex: 'paramType', width: 100, render: (v, r, idx) => (
                                        <Select size="small" value={v} onChange={val => this.handleParamChange(idx, 'paramType', val)} options={['string','number','integer','boolean','array','object'].map(x => ({label:x, value:x}))} />
                                    )},
                                    { title: '用途描述 (给AI看)', dataIndex: 'description', render: (v, r, idx) => <Input size="small" value={v} onChange={e => this.handleParamChange(idx, 'description', e.target.value)} /> },
                                    { title: '', width: 50, render: (_, r, idx) => <Button size="small" danger type="text" icon={<DeleteOutlined/>} onClick={() => this.removeParam(idx)} /> }
                                ]}
                            />
                            <Button type="dashed" block size="small" icon={<PlusOutlined />} onClick={this.addEmptyParam} style={{ marginTop: 8 }}>
                                手工添加一行
                            </Button>
                        </div>

                        {/* 输出参数 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
                                4. 模型输出参数 (告诉AI返回哪些字段)
                            </div>

                            <Table
                                size="small"
                                pagination={false}
                                dataSource={this.state.parsedOutputs}
                                rowKey={(r, idx) => idx}
                                columns={[
                                    { title: '参数名称', dataIndex: 'paramName', width: 140, render: (v, r, idx) => <Input size="small" value={v} onChange={e => this.handleOutputChange(idx, 'paramName', e.target.value)} /> },
                                    { title: '类型', dataIndex: 'paramType', width: 100, render: (v, r, idx) => (
                                        <Select size="small" value={v} onChange={val => this.handleOutputChange(idx, 'paramType', val)} options={['string','number','integer','boolean','array','object'].map(x => ({label:x, value:x}))} />
                                    )},
                                    { title: '用途描述 (给AI看)', dataIndex: 'description', render: (v, r, idx) => <Input size="small" value={v} onChange={e => this.handleOutputChange(idx, 'description', e.target.value)} /> },
                                    { title: '', width: 50, render: (_, r, idx) => <Button size="small" danger type="text" icon={<DeleteOutlined/>} onClick={() => this.removeOutput(idx)} /> }
                                ]}
                            />
                            <Button type="dashed" block size="small" icon={<PlusOutlined />} onClick={this.addEmptyOutput} style={{ marginTop: 8 }}>
                                手工添加一行
                            </Button>
                        </div>

                        <Form.Item style={{ marginTop: 32 }}>
                            <Button type="primary" size="large" htmlType="submit" loading={saving} block icon={<ThunderboltOutlined/>}>
                                一键保存并立刻上线！
                            </Button>
                        </Form.Item>
                    </Form>
                </Drawer>
            </div>
        )
    }
}
